import { Box, colors, lighten, Theme, useTheme } from "common/components";
import { compareDays, getField } from "common/utils";
import {
  FieldVariant,
  ProductLifecycleData,
  ProductLifecyclePeriod,
} from "core/api";
import dayjs, { Dayjs } from "dayjs";
import { TFunction, useTranslation } from "i18n";
import { ReactElement, useMemo } from "react";
import { ProductLifecycleActionMode } from "../../api/enums/ProductLifecycleActionMode";
import * as FlexTimeline from "./FlexTimeline";
export interface ProductLifecycleTimelineProps {
  /** The product's lifecycle data */
  productLifecycleData: ProductLifecycleData | undefined;

  /** (optional) The zIndex to use to display the mark and period labels.
   * Should be set to the zIndex of the dialog in which the component is displayed.
   * @see FlexTimelineProps.tooltipsZIndex
   */
  timelineZIndex?: number;
}

/**
 * This widget displays product lifecycle data (start and end of commitment period,
 * end of regret period, start of notice period) and related events on a timeline.
 *
 * @see {@link https://erate.atlassian.net/wiki/spaces/BE/pages/1485045767/Product+Lifecycle+Widget+IP Functional Specification}
 *
 * @remarks
 * - The widget gets the product lifecycle data from a configurable REST API URL
 * - The helper function names in the code below (for example: *createTimelineDataForCase_A_1_2_2*)
 *   refer to specific cases that are documented on the following page:
 *   {@link https://erate.atlassian.net/wiki/spaces/BE/pages/1855291393/Product+Lifecycle+Widget+-+UI+Design UI Design}
 */
export function ProductLifecycleTimeline({
  productLifecycleData,
  timelineZIndex,
}: ProductLifecycleTimelineProps): ReactElement {
  const theme = useTheme();
  const { t } = useTranslation(["common"]);

  const timelineData = useMemo(() => {
    const td = createTimelineData(productLifecycleData, t, theme);

    // Apply specific functionality related to the actionMode
    addActionModeFeatures(td, productLifecycleData, t, theme);
    return td;
  }, [productLifecycleData, t, theme]);

  return (
    <>
      {timelineData && (
        <Box sx={{ height: "100%", width: "100%" }}>
          <FlexTimeline.FlexTimeline
            positionMin={POSITION.VERY_FAR_LEFT}
            positionMax={POSITION.VERY_FAR_RIGHT}
            periodLevels={timelineData.periodLevels}
            defaultPeriodLevelHeight={80}
            marks={timelineData.marks}
            cursors={timelineData.cursors}
            timelineHeight={10}
            tooltipsZIndex={timelineZIndex}
          />
        </Box>
      )}
    </>
  );
}

interface TimelineData {
  /** A set of properties that contain all the marks that are relevant
   * to this product's lifecycle */
  bindMarks: BindMarks;

  /** An array containing only the Marks that should be displayed
   * on the FlexTimeline */
  marks: FlexTimeline.Mark[] | undefined;

  /** (optional) An array containing the Cursors. Usually it contains at least
   * one Cursor that represents the current date. */
  cursors: FlexTimeline.Cursor[] | undefined;

  /** (optional) A Cursor representing the current date */
  cursorCurrentDate?: FlexTimeline.Cursor;

  /** A set of properties that contain all the periods that are relevant
   * to this product's lifecycle */
  periods: Periods;

  /** Periods to be displayed on the FlexTimeline */
  periodLevels: FlexTimeline.PeriodLevel[] | undefined;

  /** The Timeline layout case that is being displayed */
  case: Case;

  /** Flag that indicates if the current date is BEFORE the Bind period Start date. */
  currentDateIsBeforeBindStart?: boolean;

  /** Flag that indicates if the current date is BEFORE the Regret period End date. */
  currentDateIsBeforeRegretEnd?: boolean;

  /** Flag that indicates if the current date is BEFORE the Latest Notice date. */
  currentDateIsBeforeLatestNoticeDate?: boolean;
}

interface BindMarks {
  /** Product activation date */
  activation?: FlexTimeline.Mark;

  /** START of the bind period (and the regret period) */
  bindStart?: FlexTimeline.Mark;

  /** END of the regret period */
  regretEnd?: FlexTimeline.Mark;

  /** LATEST notice date (WITHIN the bind period) */
  latestNoticeWithinBind?: FlexTimeline.Mark;

  /** END of the bind period */
  bindEnd?: FlexTimeline.Mark;

  /** Date corresponding to 2 periods (usually months) before the earliest termination date.
   *  Used to add context to the timeline */
  twoPeriodsBeforeTermination?: FlexTimeline.Mark;

  /** Date corresponding to one month BEFORE the earliest termination date.
   *  Used to add context to the timeline */
  oneMonthBeforeTermination?: FlexTimeline.Mark;

  /** Date corresponding to one month AFTER the earliest termination date.
   *  Used to add context to the timeline */
  oneMonthAfterTermination?: FlexTimeline.Mark;

  /** LATEST notice date (AFTER the bind period) */
  latestNoticeAfterBind?: FlexTimeline.Mark;

  /** EARLIEST Termination Date */
  earliestTermination?: FlexTimeline.Mark;
}
interface Periods {
  /** A period that represents the time during which the product is active */
  active?: FlexTimeline.Period;

  /** The bind period */
  bind?: FlexTimeline.Period;

  /** The regret period */
  regret?: FlexTimeline.Period;

  /** The notice period, when the current date is WITHIN the bind period */
  noticeWithinBind?: FlexTimeline.Period;

  /** A one-day period that represents the latest notice date */
  latestNoticeDate?: FlexTimeline.Period;

  /** An elastic period that goes from the current date to the latest notice date */
  preNotice?: FlexTimeline.Period;

  /** The notice period, when the current date is AFTER the bind period */
  noticeAfterBind?: FlexTimeline.Period;
}

enum Case {
  CASE_EMPTY = 0,
  CASE_A_1_1_1_1 = 1,
  CASE_A_1_1_1_2 = 2,
  CASE_A_1_1_2_1_1 = 3,
  CASE_A_1_1_2_1_2 = 4,
  CASE_A_1_1_2_2 = 5,
  CASE_A_1_2_1 = 6,
  CASE_A_1_2_2 = 7,
  CASE_A_2_1_1 = 8,
  CASE_A_2_1_2 = 9,
  CASE_A_2_2_1 = 10,
  CASE_A_2_2_2 = 11,
  CASE_B_1_1 = 12,
  CASE_B_1_2 = 13,
  CASE_B_2_1 = 14,
  CASE_B_2_2 = 15,
}

/**
 * The FlexTimeline uses integer values to horizontally position the objects
 * (Marks, Periods, Cursor) on the timeline. The integer values must be between
 * POSITION.VERY_FAR_LEFT and POSITION.VERY_FAR_RIGHT, which are parameters that are passed as input
 * to the FlexTimeline.
 * The ProductLifecycleTimeline uses values between POSITION.VERY_FAR_LEFT = 0 and POSITION.VERY_FAR_RIGHT = 1200.
 * The objects like the Marks and the Periods are always positioned on
 * predefined horizontal positions: 0, 200, 400, 600, 800, 1000 and 1200.
 * The constants below correspond to the pre-defined positions.
 * The Cursor can be positioned in between the predefined positions
 * (by means of the calculateRelativePosition() function).
 */
const POSITION = {
  VERY_FAR_LEFT: 0,
  FIRST_CURSOR: 70,
  FAR_LEFT: 200,
  LEFT: 400,
  CENTER: 600,
  RIGHT: 800,
  FAR_RIGHT: 1000,
  LAST_CURSOR: 1130,
  VERY_FAR_RIGHT: 1200,
};

const LABEL_WIDTH = {
  BIND_START: 70,
  BIND_END: 70,
  LATEST_REGRET: 80,
  LATEST_NOTICE: 80,
  ACTIVATION: 100,
  LATEST_NOTICE_WITHIN_BIND: 100,
  EARLIEST_TERMINATION: 100,
};

const CURSOR = {
  /** Vertical position for the cursor label */
  LABEL_POSITION: {
    /** Just under the timeline */
    JUST_UNDER: 10,

    /** Just under the periods */
    JUST_UNDER_PERIODS: 80,

    /** Far under the timeline, i.e. under the mark labels */
    FAR_UNDER: 200,
  },

  /** Vertical position for the cursor description */
  DESCRIPTION_POSITION: {
    /** Just above the timeline */
    JUST_ABOVE: 30,

    /** Just above the Periods */
    JUST_ABOVE_PERIODS: 70,

    /**  Far above the timeline, i.e. above the period tooltips */
    FAR_ABOVE: 300,
  },

  /** width for the cursor description tooltip */
  DESCRIPTION_WIDTH: {
    /** Short width (when displayed close to the left border) */
    SMALL: 80,

    /** Special width used for extra cursors */
    EXTRA: 100,

    /** Medium width, use when displayed close to the left border */
    MEDIUM: 150,
  },

  /** data-cy values used for the Cursors */
  DATA_CY: {
    /** Default cursor that represents the current date */
    TODAY: "today",

    /** Extra cursor that represents the Deactivation Date */
    DEACTIVATION: "deactivation",

    /** Extra cursor that represents the Adjusted Bind End Date */
    ADJUSTED_BIND_END: "adjustedBindEnd",
  },
};

const COLOR = {
  ADJUSTED_BIND_PERIOD: colors.orange[900],
};

/**
 * Helper function that creates the marks, cursor and period levels that are
 * passed to the FlexTimeline component.
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels,
 *   or undefined if productLifecycleData is undefined.
 */
function createTimelineData(
  productLifecycleData: ProductLifecycleData | undefined,
  t: TFunction<["common"]>,
  theme: Theme
): TimelineData | undefined {
  if (!productLifecycleData) {
    return undefined;
  }

  const currentDate = dayjs(productLifecycleData.currentDate);

  if (productLifecycleData.empty) {
    return createTimelineDataForEmptyLifecycle(
      productLifecycleData,
      currentDate,
      t,
      theme
    );
  }

  if (
    productLifecycleData.bindPeriod.periodLength > 0 &&
    productLifecycleData.commitmentStartDate !== undefined &&
    productLifecycleData.commitmentEndDate !== undefined
  ) {
    // Case A: Product has a Bind Period
    if (
      currentDate.isBefore(productLifecycleData.commitmentEndDate.date, "day")
    ) {
      // Case A.1: Current Date is BEFORE or WITHIN the Bind Period
      if (productLifecycleData.noticePeriodWithinBind.periodLength > 0) {
        // Case A.1.1: Bind Period with Notice Period
        if (
          currentDate.isBefore(
            dayjs(productLifecycleData.noticeDate.date),
            "day"
          )
        ) {
          // Case A.1.1.1: Current Date is BEFORE the Latest Notice Date Within Bind
          if (productLifecycleData.regretPeriod.periodLength > 0) {
            // Case A.1.1.1.1: Bind Period with Regret Period and Notice Period
            return createTimelineDataForCase_A_1_1_1_1(
              currentDate,
              productLifecycleData,
              t,
              theme
            );
          } else {
            // Case A.1.1.1.2: Bind Period without Regret Period, but with Notice Period
            return createTimelineDataForCase_A_1_1_1_2(
              currentDate,
              productLifecycleData,
              t,
              theme
            );
          }
        } else {
          // Case A.1.1.2: Current Date is AFTER the Latest Notice Date within bind,
          // but BEFORE the Bind Period End
          if (productLifecycleData.noticePeriodAfterBind.periodLength > 0) {
            // Case A.1.1.2.1: Bind Period with Notice Period within bind and after bind
            if (productLifecycleData.terminationAdjusted) {
              // Case A.1.1.2.1.1: Bind with Adjusted Notice Period After Bind
              return createTimelineDataForCase_A_1_1_2_1_1(
                currentDate,
                productLifecycleData,
                t,
                theme
              );
            } else {
              // Case A.1.1.2.1.2: Bind with non-adjusted Notice Period After Bind
              return createTimelineDataForCase_A_1_1_2_1_2(
                currentDate,
                productLifecycleData,
                t,
                theme
              );
            }
          } else {
            // Case A.1.1.2.2: Bind Period with Notice Period within bind and zero-length after bind
            return createTimelineDataForCase_A_1_1_2_2(
              currentDate,
              productLifecycleData,
              t,
              theme
            );
          }
        }
      } else {
        // Case A.1.2: Bind Period without Notice Period
        if (productLifecycleData.regretPeriod.periodLength > 0) {
          // Case A.1.2.1: Bind Period with Regret Period but without Notice Period
          return createTimelineDataForCase_A_1_2_1(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        } else {
          // Case A.1.2.2: Bind Period without Regret Period nor Notice Period
          return createTimelineDataForCase_A_1_2_2(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        }
      }
    } else {
      // Case A.2: Current Date is AFTER the Bind Period
      if (productLifecycleData.noticePeriodAfterBind.periodLength > 0) {
        // Case A.2.1: Bind with notice period
        if (productLifecycleData.terminationAdjusted) {
          // Case A.2.1.1: Bind with notice period + adjustment to period end
          return createTimelineDataForCase_A_2_1_1(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        } else {
          // Case A.2.1.2: Bind with non adjusted notice period
          return createTimelineDataForCase_A_2_1_2(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        }
      } else {
        // Case A.2.2: Bind with zero-length notice period
        if (productLifecycleData.terminationAdjusted) {
          // Case A.2.2.1: Bind with notice period adjustment
          return createTimelineDataForCase_A_2_2_1(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        } else {
          // Case A.2.2.2: Bind nor notice period
          return createTimelineDataForCase_A_2_2_2(
            currentDate,
            productLifecycleData,
            t,
            theme
          );
        }
      }
    }
  } else {
    // Case B: Product does NOT have a Bind Period
    if (productLifecycleData.noticePeriodAfterBind.periodLength > 0) {
      // Case B.1: Notice period length is bigger than zero
      if (productLifecycleData.terminationAdjusted) {
        // Case B.1.1
        return createTimelineDataForCase_B_1_1(
          currentDate,
          productLifecycleData,
          t,
          theme
        );
      } else {
        // Case B.1.2
        return createTimelineDataForCase_B_1_2(
          currentDate,
          productLifecycleData,
          t,
          theme
        );
      }
    } else {
      // Case B.2: Notice period length is equal to zero
      if (productLifecycleData.terminationAdjusted) {
        // Case B.2.1
        return createTimelineDataForCase_B_2_1(
          currentDate,
          productLifecycleData,
          t,
          theme
        );
      } else {
        // Case B.2.2
        return createTimelineDataForCase_B_2_2(
          productLifecycleData,
          currentDate,
          t,
          theme
        );
      }
    }
  }
}

/**
 * Helper function that creates the marks, cursor and period levels for the case
 * where the product lifecycle is empty.
 *
 * @param currentDate The current date as set by the UI
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForEmptyLifecycle(
  productLifecycleData: ProductLifecycleData,
  currentDate: Dayjs,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  // If activation and earliestTermination bindMarks exist, we can use Case B.2.2
  const markActivation = createMarkForActivation(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);
  if (markActivation !== undefined && markEarliestTermination !== undefined) {
    return createTimelineDataForCase_B_2_2(
      productLifecycleData,
      currentDate,
      t,
      theme
    );
  }
  // Create an array of Marks with regular dates before and after
  // the current date.
  const twoMonthBeforeCurrentDate = currentDate.add(-2, "month");
  const oneMonthBeforeCurrentDate = currentDate.add(-1, "month");
  const oneMonthAfterCurrentDate = currentDate.add(1, "month");
  const twoMonthAfterCurrentDate = currentDate.add(2, "month");
  const marks = [
    FlexTimeline.newMark(
      POSITION.FAR_LEFT,
      twoMonthBeforeCurrentDate,
      "twoMonthsBeforeToday",
      false,
      CURSOR.LABEL_POSITION.JUST_UNDER
    ),
    FlexTimeline.newMark(
      POSITION.LEFT,
      oneMonthBeforeCurrentDate,
      "oneMonthBeforeToday",
      false,
      CURSOR.LABEL_POSITION.JUST_UNDER
    ),
    FlexTimeline.newMark(
      POSITION.RIGHT,
      oneMonthAfterCurrentDate,
      "oneMonthAfterToday",
      false,
      CURSOR.LABEL_POSITION.JUST_UNDER
    ),
    FlexTimeline.newMark(
      POSITION.FAR_RIGHT,
      twoMonthAfterCurrentDate,
      "twoMonthsAfterToday",
      false,
      CURSOR.LABEL_POSITION.JUST_UNDER
    ),
  ];

  const cursor = FlexTimeline.newCursor(
    POSITION.CENTER,
    currentDate,
    "today",
    t("common:productLifeCycle.cursors.today"),
    theme,
    CURSOR.LABEL_POSITION.JUST_UNDER,
    CURSOR.DESCRIPTION_POSITION.JUST_ABOVE,
    CURSOR.DESCRIPTION_WIDTH.MEDIUM
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    []
  );

  return {
    case: Case.CASE_EMPTY,
    bindMarks: {
      activation: markActivation,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {},
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.1.1.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_1_1_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindStart = createMarkForBindStart(productLifecycleData);
  const markRegretEnd = createMarkForRegretEnd(productLifecycleData);
  const markLatestNoticeWithinBind =
    createMarkForLatestNoticeWithinBind(productLifecycleData);
  const markBindEnd = createMarkForBindEnd(productLifecycleData);
  const marks = createArrayWithDefined(
    markBindStart,
    markRegretEnd,
    markLatestNoticeWithinBind,
    markBindEnd
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  const currentDateIsBeforeBindStart =
    markBindStart !== undefined &&
    currentDate.isBefore(markBindStart.date, "day");
  const currentDateIsBeforeRegretEnd =
    markRegretEnd !== undefined &&
    currentDate.isBefore(markRegretEnd.date, "day");
  const currentDateIsBeforeLatestNoticeDate =
    markLatestNoticeWithinBind !== undefined &&
    currentDate.isBefore(markLatestNoticeWithinBind.date, "day");

  const bindPeriod = createBindPeriod(
    productLifecycleData,
    markBindStart,
    markBindEnd,
    t
  );
  const regretPeriod = createRegretPeriod(
    productLifecycleData,
    markBindStart,
    markRegretEnd,
    t
  );
  const noticePeriod = createNoticePeriodWithinBind(
    productLifecycleData,
    markLatestNoticeWithinBind,
    markBindEnd,
    t
  );

  let cursor: FlexTimeline.Cursor | undefined;
  if (currentDateIsBeforeBindStart) {
    // 1st LAYOUT OPTION: current date is BEFORE the Bind Period Start Date

    cursor = createCursorBeforeBindPeriod(
      markBindStart,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.before")}`,
      theme
    );

    // Remove the 'to' label from the Notice period because in this case
    // the 'to' label of the Bind Period should be displayed
    if (noticePeriod !== undefined) {
      noticePeriod.labelTo = undefined;
    }
  } else if (currentDateIsBeforeRegretEnd) {
    // 2nd LAYOUT OPTION: current date is WITHIN the REGRET PERIOD

    // Create a cursor that represents the current date
    // Position cursor between bind period start and regret end date
    cursor = createRelativePositionCursor(
      markBindStart,
      markRegretEnd,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.regret.within")}`,
      theme
    );

    // Enable tooltip description on Regret Period
    if (regretPeriod !== undefined) {
      regretPeriod.description = t(
        "common:productLifeCycle.periods.regret.description"
      );
    }

    // Remove the 'to' label from the Notice period because in this case
    // the 'to' label of the Bind Period should be displayed
    if (noticePeriod !== undefined) {
      noticePeriod.labelTo = undefined;
    }
  } else if (currentDateIsBeforeLatestNoticeDate) {
    // 3rd LAYOUT OPTION: current date is BETWEEN Latest Regret Date
    // and the Latest Notice Date

    // Remove the 'to' label from the Bind Period because in this case
    // the 'to' label of the Notice Period should be displayed
    if (bindPeriod !== undefined) {
      bindPeriod.labelTo = undefined;
      // Enable the tooltip description on the Bind Period
      bindPeriod.description = t(
        "common:productLifeCycle.periods.bind.description"
      );
    }

    // Enable tooltip description on Notice Period
    if (noticePeriod !== undefined) {
      noticePeriod.description = t(
        "common:productLifeCycle.periods.notice.description.beforeLatestNoticeDate"
      );
    }

    // The 'current date' cursor is positioned between regret period end and latest notice date.
    cursor = createRelativePositionCursor(
      markRegretEnd,
      markLatestNoticeWithinBind,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.within")}`,
      theme
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [regretPeriod, noticePeriod]
  );

  return {
    case: Case.CASE_A_1_1_1_1,
    bindMarks: {
      bindStart: markBindStart,
      regretEnd: markRegretEnd,
      latestNoticeWithinBind: markLatestNoticeWithinBind,
      bindEnd: markBindEnd,
    },

    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      bind: bindPeriod,
      regret: regretPeriod,
      noticeWithinBind: noticePeriod,
    },
    periodLevels,
    currentDateIsBeforeBindStart,
    currentDateIsBeforeLatestNoticeDate,
    currentDateIsBeforeRegretEnd,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.1.1.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_1_1_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindStart = createMarkForBindStart(productLifecycleData);
  const markLatestNoticeWithinBind =
    createMarkForLatestNoticeWithinBind(productLifecycleData);
  const markBindEnd = createMarkForBindEnd(productLifecycleData);
  const marks = createArrayWithDefined(
    markBindStart,
    markLatestNoticeWithinBind,
    markBindEnd
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  const currentDateIsBeforeBindStart =
    markBindStart !== undefined &&
    currentDate.isBefore(markBindStart.date, "day");
  const bindPeriod = createBindPeriod(
    productLifecycleData,
    markBindStart,
    markBindEnd,
    t
  );
  const noticePeriod = createNoticePeriodWithinBind(
    productLifecycleData,
    markLatestNoticeWithinBind,
    markBindEnd,
    t
  );
  let cursor: FlexTimeline.Cursor | undefined;

  if (currentDateIsBeforeBindStart) {
    cursor = createCursorBeforeBindPeriod(
      markBindStart,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.before")}`,
      theme
    );

    // Remove the 'to' label from the Notice period because in this case
    // the 'to' label of the Bind Period should be displayed
    if (noticePeriod !== undefined) {
      noticePeriod.labelTo = undefined;
    }
  } else {
    // Remove the 'to' label from the Bind Period because in this case
    // the 'to' label of the Notice Period should be displayed.
    if (bindPeriod !== undefined) {
      bindPeriod.labelTo = undefined;
      // Enable the tooltip description on the Bind Period
      bindPeriod.description = t(
        "common:productLifeCycle.periods.bind.description"
      );
    }

    // Enable tooltip description on Notice Period
    if (noticePeriod !== undefined) {
      noticePeriod.description = t(
        "common:productLifeCycle.periods.notice.description.beforeLatestNoticeDate"
      );
    }

    // Position cursor between bind period start and latest notice date
    cursor = createRelativePositionCursor(
      markBindStart,
      markLatestNoticeWithinBind,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.within")}`,
      theme
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [noticePeriod]
  );

  return {
    case: Case.CASE_A_1_1_1_2,
    bindMarks: {
      bindStart: markBindStart,
      latestNoticeWithinBind: markLatestNoticeWithinBind,
      bindEnd: markBindEnd,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { bind: bindPeriod, noticeWithinBind: noticePeriod },
    periodLevels,
    currentDateIsBeforeBindStart,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.1.2.1.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_1_2_1_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markLatestNoticeWithinBind = createMarkForLatestNoticeWithinBind(
    productLifecycleData,
    POSITION.LEFT
  );
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.RIGHT
  );
  const markLatestNoticeAfterBind =
    createMarkForLatestNoticeAfterBind(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markLatestNoticeWithinBind,
    markLatestNoticeAfterBind,
    markBindEnd,
    markEarliestTermination
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  let cursor: FlexTimeline.Cursor | undefined;

  const isLatestNoticeDateBeforeBindPeriodEndDate =
    markLatestNoticeAfterBind !== undefined &&
    markBindEnd !== undefined &&
    markLatestNoticeAfterBind.date.isBefore(markBindEnd.date, "day");

  if (isLatestNoticeDateBeforeBindPeriodEndDate) {
    // Position cursor between Bind Period Notice Date and Latest Notice Date
    cursor = createRelativePositionCursor(
      markLatestNoticeWithinBind,
      markLatestNoticeAfterBind,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.notice.within")}`,
      theme
    );
  } else {
    // Invert positions of markLatestNoticeAfterBind and markBindEnd
    if (markBindEnd !== undefined) {
      markBindEnd.position = POSITION.CENTER;
    }
    if (markLatestNoticeAfterBind !== undefined) {
      markLatestNoticeAfterBind.position = POSITION.RIGHT;
    }
    // Position cursor between Bind Period Notice Date and Bind Period End
    cursor = createRelativePositionCursor(
      markLatestNoticeWithinBind,
      markBindEnd,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.notice.within")}`,
      theme
    );
  }

  // In this case only the end of the Bind Period is displayed
  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );

  const latestNoticeDate = createLatestNoticeDatePeriod(
    markLatestNoticeWithinBind,
    t
  );

  // Add preNotice period
  const preNoticePeriod = createPreNoticePeriod(
    markLatestNoticeAfterBind,
    t,
    cursor
  );

  // Add notice period
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,
    t,
    cursor
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [latestNoticeDate, noticePeriod],
    // Level 2
    [preNoticePeriod]
  );

  return {
    case: Case.CASE_A_1_1_2_1_1,
    bindMarks: {
      latestNoticeWithinBind: markLatestNoticeWithinBind,
      bindEnd: markBindEnd,
      latestNoticeAfterBind: markLatestNoticeAfterBind,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      bind: bindPeriod,
      latestNoticeDate,
      noticeAfterBind: noticePeriod,
      preNotice: preNoticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.1.2.1.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_1_2_1_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markLatestNoticeWithinBind = createMarkForLatestNoticeWithinBind(
    productLifecycleData,
    POSITION.LEFT
  );
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.RIGHT
  );
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markLatestNoticeWithinBind,
    markBindEnd,
    markEarliestTermination
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  // In this case only the end of the Bind Period is displayed
  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );
  const latestNoticeDate = createLatestNoticeDatePeriod(
    markLatestNoticeWithinBind,
    t
  );

  // Position cursor between Bind Period Notice Date and Bind Period End
  const cursor = createRelativePositionCursor(
    markLatestNoticeWithinBind,
    markBindEnd,
    currentDate,
    `${prefix}${t("common:productLifeCycle.periods.notice.within")}`,
    theme
  );

  // Add notice period with tooltip description
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,
    t,
    cursor
  );
  if (noticePeriod !== undefined) {
    noticePeriod.description = t(
      "common:productLifeCycle.periods.notice.description.withoutAdjustment"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [latestNoticeDate, noticePeriod]
  );

  return {
    case: Case.CASE_A_1_1_2_1_2,
    bindMarks: {
      latestNoticeWithinBind: markLatestNoticeWithinBind,
      bindEnd: markBindEnd,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      bind: bindPeriod,
      latestNoticeDate,
      noticeAfterBind: noticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.1.2.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_1_2_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markLatestNoticeWithinBind = createMarkForLatestNoticeWithinBind(
    productLifecycleData,
    POSITION.LEFT
  );
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.RIGHT
  );
  const marks = createArrayWithDefined(markLatestNoticeWithinBind, markBindEnd);

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  // In this case only the end of the Bind Period is displayed
  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );
  // Replace Bind Period End label with 'Earliest Termination Date'
  if (bindPeriod !== undefined) {
    bindPeriod.labelTo = t("common:productLifeCycle.marks.earliestTermination");
    bindPeriod.labelToWidth = LABEL_WIDTH.EARLIEST_TERMINATION;
    // Enable tooltip description
    bindPeriod.description = t(
      "common:productLifeCycle.periods.bind.description"
    );
  }

  const latestNoticeDate = createLatestNoticeDatePeriod(
    markLatestNoticeWithinBind,
    t
  );

  // Position cursor between Bind Period Notice Date and the bind period end
  const cursor = createRelativePositionCursor(
    markLatestNoticeWithinBind,
    markBindEnd,
    currentDate,
    `${prefix}${t("common:productLifeCycle.periods.notice.within")}`,
    theme
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [latestNoticeDate]
  );

  return {
    case: Case.CASE_A_1_1_2_2,
    bindMarks: {
      latestNoticeWithinBind: markLatestNoticeWithinBind,
      bindEnd: markBindEnd,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { bind: bindPeriod, latestNoticeDate },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.2.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_2_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindStart = createMarkForBindStart(productLifecycleData);
  const markBindEnd = createMarkForBindEnd(productLifecycleData);
  const markRegretEnd = createMarkForRegretEnd(productLifecycleData);

  const marks = createArrayWithDefined(
    markBindStart,
    markRegretEnd,
    markBindEnd
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  const currentDateIsBeforeBindStart =
    markBindStart !== undefined &&
    currentDate.isBefore(markBindStart.date, "day");
  const currentDateIsBeforeRegretEnd =
    markRegretEnd !== undefined &&
    currentDate.isBefore(markRegretEnd.date, "day");

  const bindPeriod = createBindPeriod(
    productLifecycleData,
    markBindStart,
    markBindEnd,
    t
  );
  const regretPeriod = createRegretPeriod(
    productLifecycleData,
    markBindStart,
    markRegretEnd,
    t
  );
  let cursor: FlexTimeline.Cursor | undefined;

  if (currentDateIsBeforeBindStart) {
    cursor = createCursorBeforeBindPeriod(
      markBindStart,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.before")}`,
      theme
    );
  } else if (currentDateIsBeforeRegretEnd) {
    // Enable tooltip description on Regret Period
    if (regretPeriod !== undefined) {
      regretPeriod.description = t(
        "common:productLifeCycle.periods.regret.description"
      );
    }

    // Position cursor between bind period start and regret end date
    cursor = createRelativePositionCursor(
      markBindStart,
      markRegretEnd,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.regret.within")}`,
      theme
    );
  } else {
    // Replace Bind Period End label with 'Earliest Termination Date'
    if (bindPeriod !== undefined) {
      bindPeriod.labelTo = t(
        "common:productLifeCycle.marks.earliestTermination"
      );
      bindPeriod.labelToWidth = LABEL_WIDTH.EARLIEST_TERMINATION;
      // Enable the tooltip description on the Bind Period
      bindPeriod.description = t(
        "common:productLifeCycle.periods.bind.description"
      );
    }

    // Position cursor between regret period end and bind period end
    cursor = createRelativePositionCursor(
      markRegretEnd,
      markBindEnd,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.within")}`,
      theme
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod],
    // Level 1
    [regretPeriod]
  );

  return {
    case: Case.CASE_A_1_2_1,
    bindMarks: {
      bindStart: markBindStart,
      regretEnd: markRegretEnd,
      bindEnd: markBindEnd,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { bind: bindPeriod, regret: regretPeriod },
    periodLevels,
    currentDateIsBeforeBindStart,
    currentDateIsBeforeRegretEnd,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.1.2.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_1_2_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindStart = createMarkForBindStart(productLifecycleData);
  const markBindEnd = createMarkForBindEnd(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(markBindStart, markBindEnd);

  const bindPeriod = createBindPeriod(
    productLifecycleData,
    markBindStart,
    markBindEnd,
    t
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  let cursor: FlexTimeline.Cursor | undefined;

  const currentDateIsBeforeBindStart =
    markBindStart && currentDate.isBefore(markBindStart.date, "day");

  if (currentDateIsBeforeBindStart) {
    cursor = createCursorBeforeBindPeriod(
      markBindStart,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.before")}`,
      theme
    );
  } else {
    cursor = createRelativePositionCursor(
      markBindStart,
      markEarliestTermination,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.within")}`,
      theme
    );
    // Overwrite labelTo with earliest termination date
    if (bindPeriod !== undefined) {
      bindPeriod.labelTo = t(
        "common:productLifeCycle.marks.earliestTermination"
      );
      bindPeriod.labelToWidth = LABEL_WIDTH.EARLIEST_TERMINATION;
      // Enable the tooltip description on the Bind Period
      bindPeriod.description = t(
        "common:productLifeCycle.periods.bind.description"
      );
    }
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [bindPeriod]
  );

  return {
    case: Case.CASE_A_1_2_2,
    bindMarks: {
      bindStart: markBindStart,
      bindEnd: markBindEnd,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { bind: bindPeriod },
    periodLevels,
    currentDateIsBeforeBindStart,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.2.1.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_2_1_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markLatestNoticeAfterBind = createMarkForLatestNoticeAfterBind(
    productLifecycleData,
    POSITION.RIGHT
  );
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.CENTER
  );
  const markTwoPeriodsBeforeTermination =
    createMarkForTwoPeriodsBeforeTermination(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markBindEnd,
    markLatestNoticeAfterBind,
    markEarliestTermination
  );

  let cursor: FlexTimeline.Cursor;
  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  if (productLifecycleData.currentJustAfterBindEndDate) {
    cursor = createRelativePositionCursor(
      markBindEnd,
      markLatestNoticeAfterBind,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
      theme
    );
  } else {
    if (markBindEnd !== undefined) {
      markBindEnd.position = POSITION.LEFT;
    }
    if (markTwoPeriodsBeforeTermination !== undefined) {
      marks.push(markTwoPeriodsBeforeTermination);
    }
    cursor = createRelativePositionCursor(
      markTwoPeriodsBeforeTermination,
      markLatestNoticeAfterBind,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
      theme
    );
  }
  // Create product activity period with undefined activation mark.
  // This will cause the 'from' position to be at POSITION.VERY_FAR_LEFT and with empty label.
  // Set the 'to' label to empty because the 'earliest termination' label will be displayed
  // by the notice period.
  const activePeriod = createActivePeriod(
    t,
    undefined,
    markEarliestTermination
  );
  activePeriod.labelTo = undefined;

  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );

  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,
    t,
    cursor
  );

  const preNoticePeriod = createPreNoticePeriod(
    markLatestNoticeAfterBind,
    t,
    cursor
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [bindPeriod, noticePeriod],
    // Level 2
    [preNoticePeriod]
  );

  return {
    case: Case.CASE_A_2_1_1,
    bindMarks: {
      latestNoticeAfterBind: markLatestNoticeAfterBind,
      bindEnd: markBindEnd,
      twoPeriodsBeforeTermination: markTwoPeriodsBeforeTermination,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      active: activePeriod,
      bind: bindPeriod,
      noticeAfterBind: noticePeriod,
      preNotice: preNoticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.2.1.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_2_1_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.CENTER
  );
  const markTwoPeriodsBeforeTermination =
    createMarkForTwoPeriodsBeforeTermination(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(markBindEnd, markEarliestTermination);

  if (!productLifecycleData.currentJustAfterBindEndDate) {
    if (markBindEnd !== undefined) {
      markBindEnd.position = POSITION.LEFT;
    }
    if (markTwoPeriodsBeforeTermination !== undefined) {
      marks.push(markTwoPeriodsBeforeTermination);
    }
  }

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  const cursor = createFixedPositionCursor(
    POSITION.RIGHT,
    currentDate,
    `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
    theme
  );

  // Create product activity period with undefined activation mark.
  // This will cause the 'from' position to be at POSITION.VERY_FAR_LEFT and with empty label.
  // Set the 'to' label to empty because the 'earliest termination' label will be displayed
  // by the notice period.
  const activePeriod = createActivePeriod(
    t,
    undefined,
    markEarliestTermination
  );
  activePeriod.labelTo = undefined;

  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );

  // Add notice period with tooltip description
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,

    t,
    cursor
  );
  if (noticePeriod !== undefined) {
    noticePeriod.description = t(
      "common:productLifeCycle.periods.notice.description.withoutAdjustment"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [bindPeriod, noticePeriod]
  );

  return {
    case: Case.CASE_A_2_1_2,
    bindMarks: {
      bindEnd: markBindEnd,
      twoPeriodsBeforeTermination: markTwoPeriodsBeforeTermination,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      active: activePeriod,
      bind: bindPeriod,
      noticeAfterBind: noticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.2.2.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_2_2_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.CENTER
  );
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(markBindEnd, markEarliestTermination);

  if (markEarliestTermination === undefined) {
    return {
      case: Case.CASE_A_2_2_1,
      bindMarks: { bindEnd: markBindEnd },
      marks: undefined,
      cursors: undefined,
      periods: {},
      periodLevels: undefined,
    };
  }

  // Create a mark one month before the earliest termination date,
  // to provide context on the timeline.
  const oneMonthBeforeTermination = FlexTimeline.newMark(
    POSITION.CENTER,
    markEarliestTermination.date.add(-1, "month"),
    "oneMonthBeforeTermination"
  );

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  let cursor: FlexTimeline.Cursor;
  if (productLifecycleData.currentJustAfterBindEndDate) {
    cursor = createRelativePositionCursor(
      markBindEnd,
      markEarliestTermination,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
      theme
    );
  } else {
    // Reposition the Bind End mark
    if (markBindEnd !== undefined) {
      markBindEnd.position = POSITION.LEFT;
    }
    // Add an extra mark
    marks.push(oneMonthBeforeTermination);
    cursor = createRelativePositionCursor(
      oneMonthBeforeTermination,
      markEarliestTermination,
      currentDate,
      `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
      theme
    );
  }

  // Create product activity period with undefined activation mark.
  // This will cause the 'from' position to be at POSITION.VERY_FAR_LEFT and with empty label.
  // Set the 'to' label to empty because the 'earliest termination' label will be displayed
  // by the notice period.
  const activePeriod = createActivePeriod(
    t,
    undefined,
    markEarliestTermination
  );
  activePeriod.labelTo = undefined;

  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );

  // Add notice period with tooltip description
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,

    t,
    cursor
  );
  if (noticePeriod !== undefined) {
    noticePeriod.description = t(
      "common:productLifeCycle.periods.notice.description.withoutAdjustment"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [bindPeriod, noticePeriod]
  );

  return {
    case: Case.CASE_A_2_2_1,
    bindMarks: {
      bindEnd: markBindEnd,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      active: activePeriod,
      bind: bindPeriod,
      noticeAfterBind: noticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case A.2.2.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_A_2_2_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markBindEnd = createMarkForBindEnd(
    productLifecycleData,
    POSITION.CENTER
  );

  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  if (markEarliestTermination === undefined) {
    return {
      case: Case.CASE_A_2_2_2,
      bindMarks: { bindEnd: markBindEnd },
      marks: undefined,
      cursors: undefined,
      periods: {},
      periodLevels: undefined,
    };
  }

  // Overwrite position of earliest termination date
  markEarliestTermination.position = POSITION.RIGHT;

  // Create 2 marks to provide context on the timeline,
  // one month before and one month after the earliest termination date
  const markOneMonthBeforeTermination = FlexTimeline.newMark(
    POSITION.CENTER,
    markEarliestTermination.date.add(-1, "month"),
    "oneMonthBeforeTermination"
  );
  const markOneMonthAfterTermination = FlexTimeline.newMark(
    POSITION.FAR_RIGHT,
    markEarliestTermination.date.add(1, "month"),
    "oneMonthAfterTermination"
  );

  const marks = createArrayWithDefined(
    markBindEnd,
    markOneMonthAfterTermination
  );

  if (!productLifecycleData.currentJustAfterBindEndDate) {
    // Reposition the Bind End mark
    if (markBindEnd !== undefined) {
      markBindEnd.position = POSITION.LEFT;
    }
    // add an extra mark
    marks.push(markOneMonthBeforeTermination);
  }

  const prefix = t("common:productLifeCycle.cursors.currentDateIs");
  const cursor = createFixedPositionCursor(
    POSITION.RIGHT,
    currentDate,
    `${prefix}${t("common:productLifeCycle.periods.bind.after")}`,
    theme
  );

  // Create product activity period with undefined activation mark.
  // This will cause the 'from' position to be at POSITION.VERY_FAR_LEFT and with empty label.
  const activePeriod = createActivePeriod(
    t,
    undefined,
    markEarliestTermination
  );
  // Enable tooltip description
  if (activePeriod !== undefined) {
    activePeriod.description = t(
      "common:productLifeCycle.periods.active.description"
    );
  }

  const bindPeriod = createEndOfBindPeriod(
    productLifecycleData,
    markBindEnd,
    t
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [bindPeriod]
  );

  return {
    case: Case.CASE_A_2_2_2,
    bindMarks: {
      bindEnd: markBindEnd,
      earliestTermination: markEarliestTermination,
      oneMonthBeforeTermination: markOneMonthBeforeTermination,
      oneMonthAfterTermination: markOneMonthAfterTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { active: activePeriod, bind: bindPeriod },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case B.1.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_B_1_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markActivation = createMarkForActivation(productLifecycleData);
  const markLatestNoticeAfterBind = createMarkForLatestNoticeAfterBind(
    productLifecycleData,
    POSITION.RIGHT
  );
  const markTwoPeriodsBeforeTermination =
    createMarkForTwoPeriodsBeforeTermination(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markActivation,
    markTwoPeriodsBeforeTermination,
    markLatestNoticeAfterBind,
    markEarliestTermination
  );

  const cursor = createRelativePositionCursor(
    markTwoPeriodsBeforeTermination,
    markLatestNoticeAfterBind,
    currentDate,
    t("common:productLifeCycle.cursors.today"),
    theme
  );

  const activePeriod = createActivePeriod(
    t,
    markActivation,
    markEarliestTermination
  );
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,
    t,
    cursor
  );
  const preNoticePeriod = createPreNoticePeriod(
    markLatestNoticeAfterBind,
    t,
    cursor
  );

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [noticePeriod],
    // Level 2
    [preNoticePeriod]
  );

  return {
    case: Case.CASE_B_1_1,
    bindMarks: {
      activation: markActivation,
      twoPeriodsBeforeTermination: markTwoPeriodsBeforeTermination,
      latestNoticeAfterBind: markLatestNoticeAfterBind,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: {
      active: activePeriod,
      noticeAfterBind: noticePeriod,
      preNotice: preNoticePeriod,
    },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case B.1.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_B_1_2(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markActivation = createMarkForActivation(productLifecycleData);
  const markTwoPeriodsBeforeTermination =
    createMarkForTwoPeriodsBeforeTermination(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markActivation,
    markTwoPeriodsBeforeTermination,
    markEarliestTermination
  );

  const cursor = createFixedPositionCursor(
    POSITION.RIGHT,
    currentDate,
    t("common:productLifeCycle.cursors.today"),
    theme
  );

  const activePeriod = createActivePeriod(
    t,
    markActivation,
    markEarliestTermination
  );

  // Add notice period with tooltip description
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,

    t,
    cursor
  );
  if (noticePeriod !== undefined) {
    noticePeriod.description = t(
      "common:productLifeCycle.periods.notice.description.withoutAdjustment"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [noticePeriod]
  );

  return {
    case: Case.CASE_B_1_2,
    bindMarks: {
      activation: markActivation,
      twoPeriodsBeforeTermination: markTwoPeriodsBeforeTermination,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { active: activePeriod, noticeAfterBind: noticePeriod },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case B.2.1 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_B_2_1(
  currentDate: Dayjs,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markActivation = createMarkForActivation(productLifecycleData);
  const markTwoPeriodsBeforeTermination =
    createMarkForTwoPeriodsBeforeTermination(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  const marks = createArrayWithDefined(
    markActivation,
    markTwoPeriodsBeforeTermination,
    markEarliestTermination
  );

  const cursor = createRelativePositionCursor(
    markTwoPeriodsBeforeTermination,
    markEarliestTermination,
    currentDate,
    t("common:productLifeCycle.cursors.today"),
    theme
  );

  const activePeriod = createActivePeriod(
    t,
    markActivation,
    markEarliestTermination
  );

  // Add notice period with tooltip description
  const noticePeriod = createNoticePeriodAfterBind(
    productLifecycleData,

    t,
    cursor
  );
  if (noticePeriod !== undefined) {
    noticePeriod.description = t(
      "common:productLifeCycle.periods.notice.description.withoutAdjustment"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod],
    // Level 1
    [noticePeriod]
  );

  return {
    case: Case.CASE_B_2_1,
    bindMarks: {
      activation: markActivation,
      twoPeriodsBeforeTermination: markTwoPeriodsBeforeTermination,
      earliestTermination: markEarliestTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { active: activePeriod, noticeAfterBind: noticePeriod },
    periodLevels,
  };
}

/**
 * Helper function that creates the marks, cursor and period levels
 * for the case B.2.2 (documented in the Product Lifecycle Widget IP)
 *
 * @param currentDate The current date as set by the UI
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @returns A TimelineData object that contains the marks, cursor and period levels.
 */
function createTimelineDataForCase_B_2_2(
  productLifecycleData: ProductLifecycleData,
  currentDate: Dayjs,
  t: TFunction<["common", "product"]>,
  theme: Theme
): TimelineData {
  const markActivation = createMarkForActivation(productLifecycleData);
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  if (markEarliestTermination === undefined) {
    return {
      case: Case.CASE_B_2_2,
      bindMarks: { activation: markActivation },
      marks: undefined,
      cursors: undefined,
      periods: {},
      periodLevels: undefined,
    };
  }

  // Overwrite position of earliest termination date
  markEarliestTermination.position = POSITION.RIGHT;

  // Create 2 marks to provide context on the timeline,
  // one month before and one month after the earliest termination date
  const markOneMonthBeforeTermination = FlexTimeline.newMark(
    POSITION.CENTER,
    markEarliestTermination.date.add(-1, "month"),
    "oneMonthBeforeTermination"
  );
  const markOneMonthAfterTermination = FlexTimeline.newMark(
    POSITION.FAR_RIGHT,
    markEarliestTermination.date.add(1, "month"),
    "oneMonthAfterTermination"
  );

  const marks = createArrayWithDefined(
    markActivation,
    markOneMonthBeforeTermination,
    markOneMonthAfterTermination
  );

  const cursor = createFixedPositionCursor(
    POSITION.RIGHT,
    currentDate,
    t("common:productLifeCycle.cursors.today"),
    theme
  );

  // Create active period with tooltip description
  const activePeriod = createActivePeriod(
    t,
    markActivation,
    markEarliestTermination
  );
  if (activePeriod !== undefined) {
    activePeriod.description = t(
      "common:productLifeCycle.periods.active.description"
    );
  }

  const periodLevels = FlexTimeline.makePeriodLevels(
    // Level 0
    [activePeriod]
  );

  return {
    case: Case.CASE_B_2_2,
    bindMarks: {
      activation: markActivation,
      oneMonthBeforeTermination: markOneMonthBeforeTermination,
      earliestTermination: markEarliestTermination,
      oneMonthAfterTermination: markOneMonthAfterTermination,
    },
    marks,
    cursors: createArrayWithDefined(cursor),
    cursorCurrentDate: cursor,
    periods: { active: activePeriod },
    periodLevels,
  };
}

/**
 * Helper function that adds some features to the timeline which are specific
 * to a specific ProductLifecycleActionMode. Features are added to *timelineData* only if
 * productLifecycleData.actionMode is NOT undefined.
 *
 * @param timelineData The TimelineData to which some features will be added
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @param theme The Theme used by the web app
 * @returns A TimelineData object that contains the marks, cursor and period levels,
 *   or undefined if productLifecycleData is undefined.
 */
function addActionModeFeatures(
  timelineData: TimelineData | undefined,
  productLifecycleData: ProductLifecycleData | undefined,
  t: TFunction<["common"]>,
  theme: Theme
) {
  if (timelineData === undefined || productLifecycleData === undefined) {
    return;
  }

  if (productLifecycleData?.actionMode !== undefined) {
    switch (productLifecycleData.actionMode) {
      case ProductLifecycleActionMode.DEACTIVATE:
        addFeaturesForDeactivate(timelineData, productLifecycleData, t, theme);
        break;
      case ProductLifecycleActionMode.RENEW:
        break;
    }
  }
}

/**
 * Helper function that adds some features to the timeline which are specific
 * to the DEACTIVATE action mode.
 * @param timelineData The TimelineData to which some features will be added
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @param theme The Theme used by the web app
 * @returns A TimelineData object that contains the marks, cursor and period levels,
 *   or undefined if productLifecycleData is undefined.
 */
function addFeaturesForDeactivate(
  timelineData: TimelineData,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common"]>,
  theme: Theme
) {
  switch (timelineData.case) {
    case Case.CASE_A_1_1_1_1:
    case Case.CASE_A_1_2_1:
      addFeaturesForDeactivateWithRegretPeriod(
        timelineData,
        productLifecycleData,
        t,
        theme
      );
      break;
    default:
      addFeaturesForDeactivateCases(
        timelineData,
        productLifecycleData,
        t,
        theme
      );
  }
}

/**
 * Helper function that adds some features to the timeline which are specific
 * to the DEACTIVATE action mode and the cases that show the Regret Period,
 * i.e. Case A.1.1.1.1. and Case A.1.2.1.
 * @param timelineData The TimelineData to which some features will be added
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @param theme The Theme used by the web app
 * @returns A TimelineData object that contains the marks, cursor and period levels,
 *   or undefined if productLifecycleData is undefined.
 */
function addFeaturesForDeactivateWithRegretPeriod(
  timelineData: TimelineData,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common"]>,
  theme: Theme
) {
  const {
    marks,
    bindMarks,
    periodLevels,
    currentDateIsBeforeBindStart,
    currentDateIsBeforeLatestNoticeDate,
    currentDateIsBeforeRegretEnd,
  } = timelineData;
  let cursorDeactivation: FlexTimeline.Cursor | undefined;
  let cursorAdjustedBindEnd: FlexTimeline.Cursor | undefined;
  let activePeriod: FlexTimeline.Period | undefined;
  let adjustedBindPeriod: FlexTimeline.Period | undefined;

  if (currentDateIsBeforeBindStart) {
    // 1st LAYOUT OPTION: current date is BEFORE the Bind Period Start Date
    // => Nothing to show because product is not yet activated
  } else if (currentDateIsBeforeRegretEnd) {
    // 2nd LAYOUT OPTION: current date is WITHIN the REGRET PERIOD
    // => Add the Deactivation Date. The AdjustedBindEndDate is irrelevant
    // because bind period is not active yet.

    if (productLifecycleData.deactivationDate) {
      // If a deactivation date is present in the product lifecycle data,
      // add a cursor that represents it
      const deactivationDate = dayjs(productLifecycleData.deactivationDate);
      cursorDeactivation = createExtraCursor(
        marks,
        POSITION.FIRST_CURSOR,
        POSITION.LAST_CURSOR,
        deactivationDate,
        t("common:productLifeCycle.cursors.deactivationDate"),
        theme,
        CURSOR.DATA_CY.DEACTIVATION,
        CURSOR.LABEL_POSITION.FAR_UNDER,
        CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
        CURSOR.DESCRIPTION_WIDTH.EXTRA,
        theme.palette.primary.dark,
        lighten(theme.palette.primary.main, 0.8)
      );
      // Create product activity period that starts at Bind Start and extends
      // until the deactivation date. Remove 'from' and 'to' labels.
      activePeriod = createActivePeriod(
        t,
        bindMarks.bindStart,
        cursorDeactivation
      );
      activePeriod.labelFrom = undefined;
      activePeriod.labelTo = undefined;
    }
  } else if (currentDateIsBeforeLatestNoticeDate) {
    // 3rd LAYOUT OPTION: current date is BETWEEN Latest Regret Date
    // and the Latest Notice Date => show both the deactivation date
    // and the Adjusted Bind End Date

    if (productLifecycleData.deactivationDate) {
      const deactivationDate = dayjs(productLifecycleData.deactivationDate);
      cursorDeactivation = createExtraCursor(
        marks,
        POSITION.FIRST_CURSOR,
        POSITION.LAST_CURSOR,
        deactivationDate,
        t("common:productLifeCycle.cursors.deactivationDate"),
        theme,
        CURSOR.DATA_CY.DEACTIVATION,
        CURSOR.LABEL_POSITION.FAR_UNDER,
        CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
        CURSOR.DESCRIPTION_WIDTH.EXTRA,
        theme.palette.primary.dark,
        lighten(theme.palette.primary.main, 0.8)
      );
      // If the deactivation date is different than the bind end date,
      // then create product activity period that starts at Bind Start and extends
      // until the deactivation date. Remove 'from' and 'to' labels.
      if (
        bindMarks.bindStart &&
        bindMarks.bindEnd &&
        cursorDeactivation.position !== bindMarks.bindEnd.position
      ) {
        activePeriod = createActivePeriod(
          t,
          bindMarks.bindStart,
          cursorDeactivation
        );
        activePeriod.labelFrom = undefined;
        activePeriod.labelTo = undefined;
      }

      if (productLifecycleData.adjustedBindEndDate) {
        const adjustedBindEndDate = dayjs(
          productLifecycleData.adjustedBindEndDate
        );
        // The Adjusted Bind End Date is only displayed if it is different from the deactivation Date
        if (!deactivationDate.isSame(adjustedBindEndDate, "day")) {
          const extendedMarks = Array.from(marks ?? []);
          if (cursorDeactivation !== undefined) {
            extendedMarks.push(cursorDeactivation);
          }
          cursorAdjustedBindEnd = createExtraCursor(
            extendedMarks,
            POSITION.FIRST_CURSOR,
            POSITION.LAST_CURSOR,
            adjustedBindEndDate,
            t("common:productLifeCycle.cursors.adjustedBindEndDate"),
            theme,
            CURSOR.DATA_CY.ADJUSTED_BIND_END,
            CURSOR.LABEL_POSITION.FAR_UNDER,
            CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
            CURSOR.DESCRIPTION_WIDTH.EXTRA,
            COLOR.ADJUSTED_BIND_PERIOD,
            lighten(COLOR.ADJUSTED_BIND_PERIOD, 0.8)
          );
          // Remove the adjusted bind end date if it is positioned at the
          // same position than the deactivation date
          if (
            cursorDeactivation &&
            cursorAdjustedBindEnd.position === cursorDeactivation.position
          ) {
            cursorAdjustedBindEnd = undefined;
          }
        }

        // If both the deactivation cursor and the adjusted bind end date cursor
        // are present, and adjusted bind end date cursor is positioned AFTER
        // the deactivation cursor, then create an extra period that represents
        // the period during which the product will still be invoiced
        if (
          cursorDeactivation &&
          cursorAdjustedBindEnd &&
          cursorDeactivation.position < cursorAdjustedBindEnd.position
        ) {
          adjustedBindPeriod = createAdjustedBindPeriod(
            t,
            cursorDeactivation,
            cursorAdjustedBindEnd
          );
        }
      }
    }
  }

  addExtraCursorsToTimeline(
    timelineData,
    t,
    cursorDeactivation,
    cursorAdjustedBindEnd
  );

  // Add the new periods to Level 0 in the periodLevels,
  // and push the two periods one level up
  if (activePeriod !== undefined || adjustedBindPeriod !== undefined) {
    timelineData.periodLevels = FlexTimeline.makePeriodLevels(
      // Level 0
      createArrayWithDefined(activePeriod, adjustedBindPeriod),
      // Level 1
      periodLevels?.at(0)?.periods,
      // Level 2
      periodLevels?.at(1)?.periods
    );
  }

  // Filter out any mark that coincides with a cursor
  removeMarksThatMatchCursorPosition(timelineData);
}

/**
 * Helper function that adds some features to the timeline which are specific
 * to the DEACTIVATE action mode and the layout Case A.1.1.2.1.1 and A.2.1.1.
 * @param timelineData The TimelineData to which some features will be added
 * @param productLifecycleData the product lifecycle data for the current product
 * @param t the function used to retrieve translations
 * @param theme The Theme used by the web app
 * @returns A TimelineData object that contains the marks, cursor and period levels,
 *   or undefined if productLifecycleData is undefined.
 */
function addFeaturesForDeactivateCases(
  timelineData: TimelineData,
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common"]>,
  theme: Theme
) {
  const { marks, periods } = timelineData;
  // If a deactivation date is present in the product lifecycle data,
  // add a cursor that represents it
  let cursorDeactivation: FlexTimeline.Cursor | undefined;
  let cursorAdjustedBindEnd: FlexTimeline.Cursor | undefined;
  let adjustedBindPeriod: FlexTimeline.Period | undefined;
  if (productLifecycleData.deactivationDate) {
    const deactivationDate = dayjs(productLifecycleData.deactivationDate);
    cursorDeactivation = createExtraCursor(
      marks,
      POSITION.FIRST_CURSOR,
      POSITION.LAST_CURSOR,
      deactivationDate,
      t("common:productLifeCycle.cursors.deactivationDate"),
      theme,
      CURSOR.DATA_CY.DEACTIVATION,
      CURSOR.LABEL_POSITION.FAR_UNDER,
      CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
      CURSOR.DESCRIPTION_WIDTH.EXTRA,
      theme.palette.primary.dark,
      lighten(theme.palette.primary.main, 0.8)
    );

    if (productLifecycleData.adjustedBindEndDate) {
      const adjustedBindEndDate = dayjs(
        productLifecycleData.adjustedBindEndDate
      );
      // The Adjusted Bind End Date is only displayed if it is different from the deactivation Date
      if (!deactivationDate.isSame(adjustedBindEndDate, "day")) {
        const extendedMarks = Array.from(marks ?? []);
        if (cursorDeactivation !== undefined) {
          extendedMarks.push(cursorDeactivation);
        }
        cursorAdjustedBindEnd = createExtraCursor(
          extendedMarks,
          POSITION.FIRST_CURSOR,
          POSITION.LAST_CURSOR,
          adjustedBindEndDate,
          t("common:productLifeCycle.cursors.adjustedBindEndDate"),
          theme,
          CURSOR.DATA_CY.ADJUSTED_BIND_END,
          CURSOR.LABEL_POSITION.FAR_UNDER,
          CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
          CURSOR.DESCRIPTION_WIDTH.EXTRA,
          COLOR.ADJUSTED_BIND_PERIOD,
          lighten(COLOR.ADJUSTED_BIND_PERIOD, 0.8)
        );

        // Remove the adjusted bind end date if it is positioned at the
        // same position than the deactivation date
        if (
          cursorDeactivation &&
          cursorAdjustedBindEnd.position === cursorDeactivation.position
        ) {
          cursorAdjustedBindEnd = undefined;
        }
      }

      // If both the deactivation cursor and the adjusted bind end date cursor
      // are present, and adjusted bind end date cursor is positioned AFTER
      // the deactivation cursor, then create an extra period that represents
      // the period during which the product will still be invoiced
      if (
        cursorDeactivation &&
        cursorAdjustedBindEnd &&
        cursorDeactivation.position < cursorAdjustedBindEnd.position
      ) {
        adjustedBindPeriod = createAdjustedBindPeriod(
          t,
          cursorDeactivation,
          cursorAdjustedBindEnd
        );
      }
    }
  }

  addExtraCursorsToTimeline(
    timelineData,
    t,
    cursorDeactivation,
    cursorAdjustedBindEnd
  );

  // If applicable, extend the Active Period until the Deactivation date.
  if (cursorDeactivation !== undefined && periods.active !== undefined) {
    periods.active.positionTo = cursorDeactivation.position;
  }

  // Add the adjustedBindPeriod to level 0, if present
  if (
    adjustedBindPeriod !== undefined &&
    timelineData.periodLevels !== undefined &&
    timelineData.periodLevels[0] !== undefined &&
    timelineData.periodLevels[0].periods !== undefined
  ) {
    timelineData.periodLevels[0].periods?.push(adjustedBindPeriod);
  }

  // Filter out any mark that coincides with a cursor
  removeMarksThatMatchCursorPosition(timelineData);
}

/**
 * If at least one of the extra cursors is present:
 * - Overwrite the description of the'current date' cursor with 'TODAY',
 * - Add the new cursors to the timeline,
 * - Remove the 'current date' cursor if its position matches the
 *   position of one of the extra cursors
 */
function addExtraCursorsToTimeline(
  timelineData: TimelineData,
  t: TFunction<["common", "product"]>,
  ...extraCursors: (FlexTimeline.Cursor | undefined)[]
) {
  const { cursorCurrentDate } = timelineData;
  if (cursorCurrentDate !== undefined) {
    for (const extraCursor of extraCursors) {
      if (extraCursor !== undefined) {
        cursorCurrentDate.description = t(
          "common:productLifeCycle.cursors.today"
        );
        if (cursorCurrentDate.date.isSame(extraCursor.date, "day")) {
          timelineData.cursorCurrentDate = undefined;
        }
      }
    }
  }
  timelineData.cursors = createArrayWithDefined(
    timelineData.cursorCurrentDate,
    ...extraCursors
  );
}

function removeMarksThatMatchCursorPosition(timelineData: TimelineData) {
  if (timelineData.cursors !== undefined && timelineData.marks !== undefined) {
    for (const cursor of timelineData.cursors) {
      timelineData.marks = timelineData.marks.filter((mark) => {
        if (mark.position === cursor.position) {
          // cursor has the same position as the mark
          // => filter out the mark and adapt the vertical position of the cursor
          cursor.relativeVerticalPositionLabel =
            CURSOR.LABEL_POSITION.JUST_UNDER_PERIODS;
          return false;
        } else {
          // cursor does not have the same position as the mark => keep the mark
          return true;
        }
      });
    }
  }
}

/**
 * Helper function that creates a Mark that represents the Product Activation Date.
 * It is positioned at POSITION.FAR_LEFT.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * or productLifecycleData.activationDate are undefined.
 */
function createMarkForActivation(
  productLifecycleData: ProductLifecycleData | undefined
): FlexTimeline.Mark | undefined {
  if (productLifecycleData && productLifecycleData.activationDate) {
    return FlexTimeline.newMark(
      POSITION.FAR_LEFT,
      dayjs(productLifecycleData.activationDate),
      "activationDate"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the Bind Start Date.
 * It is positioned at POSITION.FAR_LEFT.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * or productLifecycleData.commitmentStartDate are undefined.
 */
function createMarkForBindStart(
  productLifecycleData: ProductLifecycleData | undefined
): FlexTimeline.Mark | undefined {
  if (productLifecycleData && productLifecycleData.commitmentStartDate) {
    const bindStartDate = dayjs(productLifecycleData.commitmentStartDate.date);
    // START of the bind period (and the regret period)
    return FlexTimeline.newMark(POSITION.FAR_LEFT, bindStartDate, "bindStart");
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the Bind End Date.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param position (optional) The horizontal position of the Mark on the Timeline.
 *   Defaults to POSITION.FAR_RIGHT.
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * or productLifecycleData.commitmentEndDate are undefined.
 */
function createMarkForBindEnd(
  productLifecycleData: ProductLifecycleData | undefined,
  position?: number
): FlexTimeline.Mark | undefined {
  if (productLifecycleData && productLifecycleData.commitmentEndDate) {
    // END of the bind period
    const bindEndDate = dayjs(productLifecycleData?.commitmentEndDate.date);
    return FlexTimeline.newMark(
      position ?? POSITION.FAR_RIGHT,
      bindEndDate,
      "bindEnd"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the Regret Period End Date.
 * The mark is positioned at POSITION.LEFT.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 *   or productLifecycleData.regretDate are undefined, or if
 *   productLifecycleData.regretPeriod.periodLength > 0
 */
function createMarkForRegretEnd(
  productLifecycleData: ProductLifecycleData | undefined
): FlexTimeline.Mark | undefined {
  if (
    productLifecycleData &&
    productLifecycleData.regretDate &&
    productLifecycleData.regretPeriod.periodLength > 0
  ) {
    const regretEndDate = dayjs(productLifecycleData.regretDate.date);
    return FlexTimeline.newMark(POSITION.LEFT, regretEndDate, "regretEnd");
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the LATEST notice date
 * WITHIN the bind period.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param position (optional) The horizontal position of the Mark on the Timeline.
 *   Defaults to POSITION.RIGHT.
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * or productLifecycleData.noticeDate are undefined
 */
function createMarkForLatestNoticeWithinBind(
  productLifecycleData: ProductLifecycleData | undefined,
  position?: number
): FlexTimeline.Mark | undefined {
  if (
    productLifecycleData &&
    productLifecycleData.noticeDate &&
    productLifecycleData.noticePeriodWithinBind &&
    productLifecycleData.noticePeriodWithinBind.periodLength > 0
  ) {
    return FlexTimeline.newMark(
      position ?? POSITION.RIGHT,
      dayjs(productLifecycleData.noticeDate.date),
      "latestNoticeWithinBind"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the LATEST notice date
 * AFTER the bind period.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param position (optional) The horizontal position of the Mark on the Timeline.
 *   Defaults to POSITION.CENTER.
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * or productLifecycleData.latestNoticeAfterBindDate are undefined.
 */
function createMarkForLatestNoticeAfterBind(
  productLifecycleData: ProductLifecycleData | undefined,
  position?: number
): FlexTimeline.Mark | undefined {
  if (productLifecycleData && productLifecycleData.latestNoticeAfterBindDate) {
    return FlexTimeline.newMark(
      position ?? POSITION.CENTER,
      dayjs(productLifecycleData.latestNoticeAfterBindDate),
      "latestNoticeAfterBind"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark located 2 periods (months) before the
 * earliest termination date. It is used to provide context on the Timeline.
 * It is positioned at POSITION.CENTER.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * is undefined.
 */
function createMarkForTwoPeriodsBeforeTermination(
  productLifecycleData: ProductLifecycleData | undefined
): FlexTimeline.Mark | undefined {
  if (
    productLifecycleData &&
    productLifecycleData.twoPeriodsBeforeTerminationDate
  ) {
    return FlexTimeline.newMark(
      POSITION.CENTER,
      dayjs(productLifecycleData.twoPeriodsBeforeTerminationDate),
      "twoPeriodsBeforeTermination"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a Mark that represents the Earliest Termination Date.
 * It is positioned at POSITION.FAR_RIGHT. If productLifecycleData.earliestTerminationDate
 * is undefined, it uses productLifecycleData.currentDate.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @returns A FlexTimeline.Mark instance, or undefined if productLifecycleData
 * is undefined.
 */
function createMarkForEarliestTermination(
  productLifecycleData: ProductLifecycleData | undefined
): FlexTimeline.Mark | undefined {
  if (productLifecycleData) {
    const earliestTerminationDate = dayjs(
      productLifecycleData.earliestTerminationDate ??
        productLifecycleData.currentDate
    );
    return FlexTimeline.newMark(
      POSITION.FAR_RIGHT,
      earliestTerminationDate,
      "earliestTermination"
    );
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates an array of elements with the given
 * elements, including only elements that are defined. Undefined elements are ignored.
 * @param elements A series of elements of type ElementType, some of which might be
 *   undefined.
 * @returns An array of objects of type ElementType, that does not include
 * any undefined elements.
 */
function createArrayWithDefined<ElementType>(
  ...elements: (ElementType | undefined)[]
): ElementType[] {
  const definedElements: ElementType[] = [];
  for (const element of elements) {
    if (element !== undefined) {
      definedElements.push(element);
    }
  }
  return definedElements;
}

/**
 * Helper function that creates a Cursor positioned exactly at the given position.
 * @param position A number that identifies a horizontal position on the timeline.
 *   Use the constants POSITION_XXX.
 * @param currentDate The current date as set by the UI
 * @param description A description for the cursor
 * @param theme The application's theme
 * @param relativeVerticalPositionLabel Used to adjust the position below the timeline
 * @param relativeVerticalPositionDescription Used to adjust the position above the timeline
 * @param tooltipWidth Width of the description
 * @param textColor Text color for the label and description
 * @param fillColor Fill color for the label and description
 * @returns a new FlexTimeline.Cursor object
 */
function createFixedPositionCursor(
  position: number,
  currentDate: Dayjs,
  description: string,
  theme: Theme,
  dataCy?: string,
  relativeVerticalPositionLabel?: number,
  relativeVerticalPositionDescription?: number,
  tooltipWidth?: number,
  textColor?: string,
  fillColor?: string
) {
  return FlexTimeline.newCursor(
    position,
    currentDate,
    dataCy ?? CURSOR.DATA_CY.TODAY,
    description,
    theme,
    relativeVerticalPositionLabel ?? CURSOR.LABEL_POSITION.FAR_UNDER,
    relativeVerticalPositionDescription ??
      CURSOR.DESCRIPTION_POSITION.FAR_ABOVE,
    tooltipWidth,
    textColor,
    fillColor
  );
}

/**
 * Helper function that creates a Cursor positioned between *firstMark*
 * and *secondMark*. The cursor, which represents the current date, will be
 * positioned proportionally to the dates of the given marks.
 * @param firstMark The first mark, with a date BEFORE the currentDate
 * @param secondMark The second mark, with a date AFTER the currentDate
 * @param currentDate The current date as set by the UI
 * @param description A description for the cursor
 * @returns a new FlexTimeline.Cursor object
 */
function createRelativePositionCursor(
  firstMark: FlexTimeline.Mark | undefined,
  secondMark: FlexTimeline.Mark | undefined,
  currentDate: Dayjs,
  description: string,
  theme: Theme,
  dataCy?: string,
  relativeVerticalPositionLabel?: number,
  relativeVerticalPositionDescription?: number,
  tooltipWidth?: number,
  textColor?: string,
  fillColor?: string
) {
  return createFixedPositionCursor(
    FlexTimeline.calculateRelativePosition(currentDate, firstMark, secondMark),
    currentDate,
    description,
    theme,
    dataCy ?? CURSOR.DATA_CY.TODAY,
    relativeVerticalPositionLabel,
    relativeVerticalPositionDescription,
    tooltipWidth,
    textColor,
    fillColor
  );
}

/**
 * Helper function that creates a Cursor positioned somewhere between the
 * provided *marks*. If *cursorDate* is between 2 marks, the cursor will
 * be positioned proportionally between the 2 marks. If *cursorDate* is
 * the same day as one of the marks, it will be positioned at the same
 * position as the mark.
 *
 * @param marks The marks that exist on the timeline
 * @param firstPosition The position for the cursor in case the *cursorDate* is
 *   BEFORE the first mark's date.
 * @param lastPosition The position for the cursor in case the *cursorDate* is
 *   AFTER the last mark's date.
 * @param cursorDate The date that the cursor represents
 * @param description A description for the cursor
 * @param theme The application's theme
 * @param relativeVerticalPositionLabel Used to adjust the position below the timeline
 * @param relativeVerticalPositionDescription Used to adjust the position above the timeline
 * @param tooltipWidth Width of the description
 * @param textColor Text color for the label and description
 * @param fillColor Fill color for the label and description
 * @returns a new FlexTimeline.Cursor object
 */
function createExtraCursor(
  marks: FlexTimeline.Mark[] | undefined,
  firstPosition: number,
  lastPosition: number,
  cursorDate: Dayjs,
  description: string,
  theme: Theme,
  dataCy?: string,
  relativeVerticalPositionLabel?: number,
  relativeVerticalPositionDescription?: number,
  tooltipWidth?: number,
  textColor?: string,
  fillColor?: string
) {
  let cursorPosition = POSITION.CENTER;
  if (marks !== undefined) {
    let firstMark, secondMark: FlexTimeline.Mark | undefined;
    const sortedMarks = marks.toSorted((markA, markB) =>
      compareDays(markA.date, markB.date)
    );
    sortedMarks.reverse();
    let nextMark = sortedMarks.pop();
    while (nextMark !== undefined) {
      if (
        cursorDate.isBefore(nextMark.date, "day") ||
        cursorDate.isSame(nextMark.date, "day")
      ) {
        secondMark = nextMark;
        break;
      } else {
        firstMark = nextMark;
        nextMark = sortedMarks.pop();
      }
    }
    if (firstMark === undefined) {
      if (secondMark && cursorDate.isSame(secondMark.date, "day")) {
        // cursorDate is exactly on the first mark
        cursorPosition = secondMark.position;
      } else {
        // cursorDate is BEFORE the first mark
        cursorPosition = firstPosition;
      }
    } else if (secondMark === undefined) {
      // cursorDate is AFTER the last mark
      cursorPosition = lastPosition;
    } else {
      // cursorDate is between the firstDate and secondDate
      cursorPosition = FlexTimeline.calculateRelativePosition(
        cursorDate,
        firstMark,
        secondMark
      );
    }
  }
  return createFixedPositionCursor(
    cursorPosition,
    cursorDate,
    description,
    theme,
    dataCy,
    relativeVerticalPositionLabel,
    relativeVerticalPositionDescription,
    tooltipWidth,
    textColor,
    fillColor
  );
}

/**
 * Creates a Cursor for the case where the current date is
 * before the start of the bind.
 *
 * @param markBindStart A Mark that represents the start of the Bind Period
 * @param currentDate Today's date
 * @param description The description that is displayed above the vertical hairline
 * @returns return a Cursor that is positioned between the leftmost point
 *   on the timeline (POSITION.VERY_FAR_LEFT) and the position of the bind start mark.
 */
function createCursorBeforeBindPeriod(
  markBindStart: FlexTimeline.Mark | undefined,
  currentDate: Dayjs,
  description: string,
  theme: Theme
): FlexTimeline.Cursor {
  return FlexTimeline.newCursor(
    (POSITION.VERY_FAR_LEFT +
      (markBindStart?.position ?? POSITION.VERY_FAR_LEFT)) /
      3,
    currentDate,
    "today",
    description,
    theme,
    CURSOR.LABEL_POSITION.JUST_UNDER,
    CURSOR.DESCRIPTION_POSITION.JUST_ABOVE,
    CURSOR.DESCRIPTION_WIDTH.SMALL
  );
}

/**
 * Helper function that creates the period during which a product is active.
 * The 'active' period extends from the activation date to the earliest termination date.
 * If the activation date is undefined, the active period will start at POSITION.VERY_FAR_LEFT.
 *
 * @param t The translations from common.ts
 * @param markFrom The mark that represents the activation date
 * @param markTo The mark that represents the earliest termination date
 * @returns A Period object that represents the product activity period
 */
function createActivePeriod(
  t: TFunction<["common", "product"]>,
  markFrom?: FlexTimeline.Mark,
  markTo?: FlexTimeline.Mark
): FlexTimeline.Period {
  return {
    dataCy: "active",
    label: "",
    positionFrom: markFrom?.position ?? POSITION.VERY_FAR_LEFT,
    labelFrom:
      markFrom !== undefined
        ? t("common:productLifeCycle.marks.activationDate")
        : undefined,
    labelFromWidth: LABEL_WIDTH.ACTIVATION,
    positionTo: markTo?.position ?? POSITION.VERY_FAR_RIGHT,
    labelTo: t("common:productLifeCycle.marks.earliestTermination"),
    labelToWidth: LABEL_WIDTH.EARLIEST_TERMINATION,
    description: "",
  };
}

/**
 * Helper function that creates a period during which a product will be invoiced
 * even though it is not active any more.
 * This period should start at Deactivation Date and extend until the Adjusted Bind End Date.
 *
 * @param t The translations from common.ts
 * @param markFrom The mark that represents the activation date
 * @param markTo The mark that represents the Adjusted Bind End Date
 * @returns A Period object that represents the Adjusted Bind Period
 */
function createAdjustedBindPeriod(
  t: TFunction<["common", "product"]>,
  markFrom?: FlexTimeline.Mark,
  markTo?: FlexTimeline.Mark
): FlexTimeline.Period {
  return {
    dataCy: "adjustedBind",
    label: "Adjusted Bind",
    positionFrom: markFrom?.position ?? POSITION.VERY_FAR_LEFT,
    // leave labelFrom undefined
    positionTo: markTo?.position ?? POSITION.VERY_FAR_RIGHT,
    // Leave labelTo undefined
    description: "",
    textColor: COLOR.ADJUSTED_BIND_PERIOD,
    fillColor: COLOR.ADJUSTED_BIND_PERIOD,
  };
}

/**
 * Helper function that creates the Bind period.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param markBindStart A Mark that represents the Bind Period Start Date
 * @param markBindEnd  A Mark that represents the Bind Period End Date
 * @param t The translations from common.ts
 * @returns A Period object that represents the Bind period,
 * or undefined if either markBindStart or markBindEnd are undefined
 */
function createBindPeriod(
  productLifecycleData: ProductLifecycleData,
  markBindStart: FlexTimeline.Mark | undefined,
  markBindEnd: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>
): FlexTimeline.Period | undefined {
  if (markBindStart !== undefined && markBindEnd !== undefined) {
    return {
      dataCy: "bind",
      label: t("common:productLifeCycle.periods.bind.label"),
      subLabel: getPeriodSubLabel(productLifecycleData.bindPeriod),
      positionFrom: markBindStart.position,
      labelFrom: t("common:productLifeCycle.marks.bindStart"),
      labelFromWidth: LABEL_WIDTH.BIND_START,
      positionTo: markBindEnd.position,
      labelTo: t("common:productLifeCycle.marks.bindEnd"),
      labelToWidth: LABEL_WIDTH.BIND_END,
      // leave description undefined
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates the end of the Bind period.
 * The end of the bind period is represented by a rounded rectangle that
 * starts at POSITION.VERY_FAR_LEFT and ends at Bind Period End.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param markBindEnd A Mark that represents the Bind Period End Date
 * @param t The translations from common.ts
 * @returns A Period object that represents the end of the Bind period,
 * or undefined if markBindEnd is undefined
 */
function createEndOfBindPeriod(
  productLifecycleData: ProductLifecycleData,
  markBindEnd: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>
): FlexTimeline.Period | undefined {
  if (markBindEnd !== undefined) {
    return {
      dataCy: "bind",
      label: t("common:productLifeCycle.periods.bind.label"),
      subLabel: getPeriodSubLabel(productLifecycleData.bindPeriod),
      labelAlignment: "left",
      positionFrom: POSITION.VERY_FAR_LEFT,
      // Leave labelFrom undefined
      positionTo: markBindEnd.position,
      labelTo: t("common:productLifeCycle.marks.bindEnd"),
      labelToWidth: LABEL_WIDTH.BIND_END,
      // Leave description undefined by default
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates the Regret period.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param t The translations from common.ts
 * @returns A Period object that represents the Regret period,
 * or undefined if either markBindStart or markRegretEnd are undefined
 */
function createRegretPeriod(
  productLifecycleData: ProductLifecycleData,
  markBindStart: FlexTimeline.Mark | undefined,
  markRegretEnd: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>
): FlexTimeline.Period | undefined {
  if (markBindStart !== undefined && markRegretEnd !== undefined) {
    return {
      dataCy: "regret",
      label: t("common:productLifeCycle.periods.regret.label"),
      subLabel: getPeriodSubLabel(productLifecycleData.regretPeriod),
      // Start of the regret period coincides with start of the bind period
      positionFrom: markBindStart.position,
      // Leave labelFrom undefined
      positionTo: markRegretEnd.position,
      labelTo: t("common:productLifeCycle.marks.latestRegret"),
      labelToWidth: LABEL_WIDTH.LATEST_REGRET,
      // Leave description undefined by default
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates the Notice period WITHIN Bind, i.e. the
 * that is applicable when the current date is WITHIN the bind period.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param markFrom The Mark at which the period starts (usually the Latest Notice Date within Bind)
 * @param markTo The Mark at which the period ends (usually the Bind Period End or the Earliest Termination Date)
 * @param t The translations from common.ts
 * @returns A Period object that represents the Notice period WITHIN Bind,
 *   or undefined if either markLatestNoticeWithinBind or markEarliestTermination are undefined.
 */
function createNoticePeriodWithinBind(
  productLifecycleData: ProductLifecycleData,
  markFrom: FlexTimeline.Mark | undefined,
  markTo: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>
): FlexTimeline.Period | undefined {
  if (markFrom !== undefined && markTo !== undefined) {
    return {
      dataCy: "noticeWithinBind",
      label: t("common:productLifeCycle.periods.notice.label"),
      subLabel: getPeriodSubLabel(productLifecycleData.noticePeriodWithinBind),
      positionFrom: markFrom.position,
      labelFrom: t("common:productLifeCycle.marks.latestNotice"),
      labelFromWidth: LABEL_WIDTH.LATEST_NOTICE,
      positionTo: markTo.position,
      labelTo: t("common:productLifeCycle.marks.earliestTermination"),
      labelToWidth: LABEL_WIDTH.EARLIEST_TERMINATION,
      // Leave description undefined by default
      descriptionPlacement: "left",
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates a one-day period representing the
 * Latest Notice Date.
 *
 * @param t The translations from common.ts
 * @returns A Period object that represents the Latest Notice Date
 * or undefined if markLatestNoticeWithinBind is undefined
 */
function createLatestNoticeDatePeriod(
  markLatestNoticeWithinBind: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>
): FlexTimeline.Period | undefined {
  if (markLatestNoticeWithinBind !== undefined) {
    return {
      dataCy: "latestNoticeDate",
      label: "",
      positionFrom: markLatestNoticeWithinBind.position - 1,
      positionTo: markLatestNoticeWithinBind.position,
      labelTo: t("common:productLifeCycle.marks.noticeDateWithinBind"),
      labelToWidth: LABEL_WIDTH.LATEST_NOTICE_WITHIN_BIND,
      // Leave description undefined
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function that creates the 'Pre-Notice' period, i.e. a period
 * that goes from the current date (represented by the cursor)
 * to the latest notice date.
 *
 * @param t The translations from common.ts
 * @param cursor The cursor that represents the current date
 * @returns A Period object that represents the Pre-Notice period
 * or undefined if either markLatestNoticeWithinBind
 * or cursor are undefined.
 */
function createPreNoticePeriod(
  markLatestNoticeAfterBind: FlexTimeline.Mark | undefined,
  t: TFunction<["common", "product"]>,
  cursor: FlexTimeline.Cursor
): FlexTimeline.Period | undefined {
  if (cursor !== undefined && markLatestNoticeAfterBind !== undefined) {
    return {
      dataCy: "preNotice",
      label: "",
      positionFrom: cursor.position,
      // Leave labelFrom undefined
      positionTo: markLatestNoticeAfterBind.position,
      labelTo: t("common:productLifeCycle.marks.latestNotice"),
      labelToWidth: LABEL_WIDTH.LATEST_NOTICE,
      description: t(
        "common:productLifeCycle.periods.notice.description.withAdjustment"
      ),
      descriptionPlacement: "left",
      opacity: 0.66,
    };
  } else {
    return undefined;
  }
}

/** Helper function that creates the Notice period AFTER BIND, i.e. the Notice
 * Period when the current date is AFTER the bind period end.
 * The Notice Period goes from the cursor's position to the earliest termination date.
 *
 * @param productLifecycleData A ProductLifecycleData object that represents
 *   the bind periods, dates and events of the lifecycle of the current product
 *   (as returned by the REST API).
 * @param t The translations from common.ts
 * @param cursor The cursor that represents the current date
 * @returns A Period object that represents the Notice period AFTER Bind
 * or undefined if either cursor or productLifecycleData are undefined.
 */
function createNoticePeriodAfterBind(
  productLifecycleData: ProductLifecycleData,
  t: TFunction<["common", "product"]>,
  cursor: FlexTimeline.Cursor
): FlexTimeline.Period | undefined {
  const markEarliestTermination =
    createMarkForEarliestTermination(productLifecycleData);

  if (cursor !== undefined && markEarliestTermination !== undefined) {
    return {
      dataCy: "noticeAfterBind",
      label: t("common:productLifeCycle.periods.notice.label"),
      subLabel: getPeriodSubLabel(productLifecycleData.noticePeriodAfterBind),
      labelAlignment: "right",
      positionFrom: cursor.position,
      // Leave labelFrom undefined
      positionTo: markEarliestTermination.position,
      labelTo: t("common:productLifeCycle.marks.earliestTermination"),
      labelToWidth: LABEL_WIDTH.EARLIEST_TERMINATION,
      // Leave description undefined by default
      descriptionPlacement: "left",
    };
  } else {
    return undefined;
  }
}

/**
 * Helper function to get the label for the bind periods.
 *
 * @param label Static part of the label
 * @param separator A string that is inserted between the *label* and the period name
 * @param plPeriod The ProductLifecyclePeriod object from which the periodName is used.
 *   If periodName is empty, a periodName is assembled using "<periodLength> <periodUnit>"
 * @returns A string with the following format: "<label><separator><periodName>".
 */
function getPeriodSubLabel(plPeriod?: ProductLifecyclePeriod) {
  if (plPeriod === undefined || plPeriod === null) {
    return "";
  }
  // Use the period name, if present
  const periodNameField = getField(
    { key: "Name", variant: FieldVariant.FIXED },
    plPeriod.fields
  );
  const periodName = periodNameField?.values[0].value;
  if (periodName && periodName.length > 0) {
    return periodName;
  }
  // If the name is undefined or empty, use the period length and period unit
  const periodUnitField = getField(
    { key: "Period_Unit", variant: FieldVariant.FIXED },
    plPeriod.fields ?? []
  );
  return `${plPeriod.periodLength} ${periodUnitField?.values[0].displayValue}`;
}
